import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { DefaultButton, Label, PrimaryButton, TextField } from '@fluentui/react';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { useUser } from '@micro-frontend-react/employee-experience/lib/useUser';
import * as React from 'react';
import { FormEvent, Context as ReactContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import * as Styled from '../../../Common/Styles/portalPage.styled';
import {onRenderServiceTreeIdLabel } from '../../../Common/Components/Labels';
import { SubscriberRecord, UserAccessTypes } from '../../../Common/ConsumerModels/SubscriberRecord';
import { HRDPEnvironment } from '../../../Common/CommonModels/HRDPEnvironmentEnum';
import { AddServicePrincipalsContainer } from './AddServicePrincipalsContainer';
import { SubscriberStatusEnum } from '../../../Common/CommonModels/SubscriberStatusEnum';

type AddSubscriberPanelProps = {
  isOpen: boolean;
  dismissPanel(): void;
  setShowConfirmationPopup(val: boolean): void;
  setShowErrorMessage(val: boolean): void;
  isUpdateMode?: boolean;
  defaultSubscriber ?: SubscriberRecord
};

export function AddSubscriberPanel(props: AddSubscriberPanelProps): React.ReactElement {
  const { isOpen, dismissPanel, setShowConfirmationPopup, setShowErrorMessage, isUpdateMode, 
    defaultSubscriber = {
      id: uuidv4(),
      name: '',
      description: '',
      type: '',
      requestorUpn: '',
      serviceTreeID: '',
      serviceTreeName: '',
      securityGroup: '',
      userAccessTypes: [] as UserAccessTypes[],
      status: SubscriberStatusEnum.Approved
    }
  } = props;

  const user = useUser();
  const history = useHistory();
  const { httpClient } = React.useContext(Context as ReactContext<IEmployeeExperienceContext>);

  // Subscriber State
  const [id] = useState<string>(defaultSubscriber.id);
  const [name, setName] = useState<string>(defaultSubscriber.name);
  const [description, setDescription] = useState<string>(defaultSubscriber.description);  
  const [serviceTreeID, setServiceTreeID] = useState<string>(defaultSubscriber.serviceTreeID);
  const [serviceTreeName, setServiceTreeName] = useState<string>(defaultSubscriber.serviceTreeName);
  const [securityGroup, setSecurityGroup] = useState<string>(defaultSubscriber.securityGroup);
  const [userAccessTypes, setUserAccessTypes] = useState<UserAccessTypes[]>(defaultSubscriber.userAccessTypes);
  const [status, setStatus] = useState<SubscriberStatusEnum>(defaultSubscriber.status);

  // Error state variables
  const [nameError, setNameError] = useState<boolean>(false);
  const [descriptionError, setDescriptionError] = useState<boolean>(false);
  const [serviceTreeIDError, setServiceTreeIDError] = useState<boolean>(false);


  // Button state
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
  const [isServiceTreeInfoLoading, setIsServiceTreeInfoLoading] = useState(false);


  // Check form validity after each render
  useEffect(() => {
    if (
      name?.length > 0 &&
      description?.length > 0 &&
      serviceTreeID?.length > 0 &&
      serviceTreeName?.length > 0 &&
      securityGroup?.length > 0 &&
      hasMSI() &&
      !isServiceTreeInfoLoading &&
      !nameError &&
      !descriptionError &&
      !serviceTreeIDError
    ){
      setIsSubmitButtonDisabled(false);
    } else {
      setIsSubmitButtonDisabled(true);
    }
  });

  const hasMSI = () => {
    var hasMSIs = false;
    userAccessTypes?.map((accessType) => {if (accessType?.servicePrincipals?.length > 0) hasMSIs = true; });
    return hasMSIs;
  };

  const onChangeSubscriberName = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setName(newValue);
  };

  const onChangeDescription = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setDescription(newValue);
  };

  const onChangeServiceTreeId = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setServiceTreeID(newValue);
  };

  const getSubscriberNameErrorMessage = (newValue?: string) => {
    if (!newValue) {
      setNameError(true);
      return 'Please enter a valid name with printable characters only.';
    } else if (/^[\x00-\x7F]*$/.test(newValue)) {
      setNameError(false);
      return '';
    } else {
      setNameError(true);
      return 'Please enter a valid name with printable characters only.';
    }
  };

  const getDescriptionErrorMessage = (newValue?: string) => {
    if (!newValue) {
      setDescriptionError(true);
      return 'Please enter a valid description with printable characters only.';
    } else if (/^[\x00-\x7F]*$/.test(newValue)) {
      setDescriptionError(false);
      return '';
    } else {
      setDescriptionError(true);
      return 'Please enter a valid description with printable characters only.';
    }
  };

  const fetchServiceTreeID = async (newValue: string) => {
    return new Promise((resolve) => {
      httpClient
        .get(`${__API_BASE_URL__}/common/servicetree/${newValue}`, {
          resource: __CLIENT_ID__,
          headers: {
            'Content-Type': 'application/json',
            'X-Content-Type-Options': 'nosniff',
          },
        })
        .then((response: any) => {
          const responseServiceTreeName = 'name' in response.data ? response.data.name : response.data.componentName;
          const serviceTreeAccess = response.data.hasFeatureTeamAccess;
          setSecurityGroup(response.data.featureTeam);
          setServiceTreeName(responseServiceTreeName);
          setServiceTreeIDError(!serviceTreeAccess);
          setIsServiceTreeInfoLoading(false);
          resolve(serviceTreeAccess ? 0 : 2);
        })
        .catch(() => {
          setSecurityGroup('');
          setServiceTreeName('');
          setServiceTreeIDError(true);
          setIsServiceTreeInfoLoading(false);
          resolve(1);
        });
    });
  };

  const getServiceTreeIDErrorMessage = async (newValue?: string) => {
    if (!newValue) {
      setServiceTreeIDError(true);
      return 'Please enter a valid Service Tree ID.';
    }
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(newValue)) {
      setServiceTreeIDError(true);
      return 'Please enter a valid Service Tree ID in GUID format.';
    }
    setIsServiceTreeInfoLoading(true);
    const result: number = (await fetchServiceTreeID(newValue)) as number;

    if (result > 0) {
      if (result === 1) return 'Invalid Service Tree ID fetched from Service Tree.';
      if (result === 2) return `Feature Team access required. You must be listed as a member of the feature team alias in Service Tree. Visit https://microsoftservicetree.com/services/${newValue}/metadata to verify your access.`;
    }

    return '';
  };

  const onSubmit = async () => {  
    return new Promise(() => {
      setShowConfirmationPopup(true);
      httpClient
        .post(`${__API_BASE_URL__}/subscriber/upsertSubscriber`, {
          resource: __CLIENT_ID__,
          headers: {
            'Content-Type': 'application/json',
            'X-Content-Type-Options': 'nosniff',
          },
          data: {
            type: 'Subscriber',
            id: id,
            name: name,
            description: description,
            requestorUpn: user.id,
            serviceTreeID: serviceTreeID,
            serviceTreeName: serviceTreeName,
            securityGroup: securityGroup,
            userAccessTypes: userAccessTypes,
            status: isUpdateMode ? SubscriberStatusEnum.PendingApproval : SubscriberStatusEnum.Approved,
          },
        })
        .then(() => {
          history.push(`/consumers/${id}`);
        })
        .catch(() => {
          setShowErrorMessage(true);
        });
    });
  };

  const reset = () => {
    setName('');
    setDescription('');
    setServiceTreeID('');
    setServiceTreeName('');
    setSecurityGroup('');
    setUserAccessTypes([]);
    setIsSubmitButtonDisabled(true);
  };

  const submitResetAndDismissPanel = async () => {
    await onSubmit();
  };

  const resetAndDismissPanel = () => {
    reset();
    dismissPanel();
  };

  const onRenderFooterContent = () => {
    return (
      <div>
        <PrimaryButton
          text="Submit"
          disabled={isSubmitButtonDisabled}
          allowDisabledFocus={true}
          onClick={submitResetAndDismissPanel}
        />
        <DefaultButton text="Cancel" allowDisabledFocus={true} onClick={resetAndDismissPanel}>
          Cancel
        </DefaultButton>
      </div>
    );
  };

  const onRenderTitleText = () => {
    if(isUpdateMode){
      return <Styled.PortalPagePanelText>Update Consumer</Styled.PortalPagePanelText>;
    }
    return <Styled.PortalPagePanelText>Add Consumer</Styled.PortalPagePanelText>;
  };

  return (
    <CoherencePanel
      data-testid="add-subscriber-panel"
      isOpen={isOpen}
      onRenderFooter={onRenderFooterContent}
      onDismiss={dismissPanel}
      onRenderHeader={onRenderTitleText}
      panelSize={CoherencePanelSize.large}
      closeButtonAriaLabel="Close"
    >
      <Styled.PortalPagePanelContainer>
        <TextField
          data-testid="add-subscriber-panel-name"
          label="Consumer Name"
          id="consumerName"
          placeholder="Enter Custom Name"
          value={name}
          required={true}
          onChange={onChangeSubscriberName}
          onGetErrorMessage={getSubscriberNameErrorMessage}
          validateOnLoad={false}
          validateOnFocusOut={true}
          disabled={ defaultSubscriber.name != '' ? true : false }
        />

        <TextField
          data-testid="add-subscriber-panel-id"
          label="Consumer ID"
          id="consumerId"
          value={id}
          required={true}
          validateOnLoad={false}
          validateOnFocusOut={true}
          disabled={true}
        />

        <TextField
          data-testid="add-subscriber-panel-description"
          label="Description"
          id="description"
          value={description}
          placeholder="Enter Custom Description"
          required={true}
          onChange={onChangeDescription}
          onGetErrorMessage={getDescriptionErrorMessage}
          validateOnLoad={false}
          validateOnFocusOut={true}
          multiline={true}
          autoAdjustHeight={true}
          disabled={ defaultSubscriber.description != '' ? true : false }
        />

        {onRenderServiceTreeIdLabel(isServiceTreeInfoLoading)}
        <TextField
          data-testid="add-subscriber-panel-serviceTreeId"
          id="serviceTreeId"
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          value={serviceTreeID}
          onChange={onChangeServiceTreeId}
          onGetErrorMessage={getServiceTreeIDErrorMessage}
          validateOnLoad={false}
          validateOnFocusOut={true}
          disabled={ defaultSubscriber.serviceTreeID != '' ? true : false }
        />

        {!isServiceTreeInfoLoading && !serviceTreeIDError && serviceTreeName?.length > 0 && (
          <TextField
            label="Service Tree Name"
            id="serviceTreeName"
            value={serviceTreeName}
            disabled={true}
            styles={{
              field: { color: '#323130' },
              subComponentStyles: { label: { root: { color: '#323130' } } },
            }}
          />
        )}

        {!isServiceTreeInfoLoading && !serviceTreeIDError && securityGroup?.length > 0 && (
          <TextField
            label="Security Group"
            id="securityGroup"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            value={securityGroup}
            disabled={true}
            styles={{
              field: { color: '#323130' },
              subComponentStyles: { label: { root: { color: '#323130' } } },
            }}
          />
        )}

        <Styled.OuterBoxComponent>            
          <Label style={{padding: '0'}}><b>Non-Production Service Principals</b></Label>
          <br/>
          <AddServicePrincipalsContainer
            environment={HRDPEnvironment.NonProduction}
            userAccessTypes={userAccessTypes}
            setUserAccessTypes={setUserAccessTypes}
          />
        </Styled.OuterBoxComponent>
        <Styled.OuterBoxComponent>            
            <Label style={{padding: '0'}}><b>Production Service Principals</b></Label>
            <br/>
            <AddServicePrincipalsContainer
              environment={HRDPEnvironment.Production}
              userAccessTypes={userAccessTypes}
              setUserAccessTypes={setUserAccessTypes}
            /> 
          </Styled.OuterBoxComponent>
        
      </Styled.PortalPagePanelContainer>
    </CoherencePanel>
  );
}
